import {jsx} from 'panel';

import {UserIconEl} from 'common/widgets/user-icon';
import {SvgIconEl} from 'common/components/svg-icon';
import {TextHighlightEl} from 'common/components/text-highlight';
import {SelectTheme} from 'common/components/select/constants';
import {SelectEl} from 'common/components/select';
import {PublicDashboardLinkRow, ShareOptionsLocation} from 'common/widgets/sharing/modal/types';
import {truncate} from 'common/util/string';

export const getWorkspaceName = ({dataView}) => {
  return <span>{truncate(dataView, 20)}</span>;
};

export const getPublicDashboardLinkEnabled = (row: PublicDashboardLinkRow) => {
  return (
    <div>
      <p sel='.public-dashboard-created'>{row.createdDate}</p>
      <p sel='.public-dashboard-creator'>by {row.creator}</p>
    </div>
  );
};

const getUserIconEl = (id: number, name: string, email: string) => (
  <UserIconEl
    attrs={{
      id,
      name,
      email,
      'disable-tooltip': true,
      size: `medium`,
    }}
  />
);

export const GetHelpLink = (marginStyle, handleHelpClick) => (
  <div
    sel='.help'
    attrs={{
      style: marginStyle,
    }}
  >
    <a
      attrs={{
        href: `https://help.mixpanel.com/hc/en-us/articles/360044299311`,
        target: `_blank`,
      }}
      on={{
        click: handleHelpClick,
      }}
    >
      Learn About Sharing
    </a>
  </div>
);

export const GetOwnerListItem = (helpers) => {
  const owner = helpers.getEntityOwner();
  const userIcon = getUserIconEl(owner.id, owner.name, owner.email);
  const displayEmail = helpers.getShareGrantEmail(owner.email);

  return (
    <li attrs={{elref: `owner`}}>
      {userIcon}
      <div sel='.display'>
        <>
          <div sel='.name'>{owner.formattedName}</div>
          <div sel='.subtitle'>{displayEmail}</div>
        </>
      </div>
      <div sel='.controls'>
        <div sel='.creator-label'>Creator</div>
      </div>
    </li>
  );
};

export const GetUpsellBanner = () => (
  <div sel='.banner'>
    <div sel='.upsell-icon-wrapper'>
      <SvgIconEl
        attrs={{
          icon: `lightning-bolt`,
          color: `green`,
        }}
      />
    </div>
    <p>
      <strong>Upgrade your plan </strong>
      to gain access to advanced sharing and additional privacy controls. On your current plan
      <strong> Starter (Free) </strong>
      everything you create will be viewable and editable by others in this project.
      <a
        attrs={{
          href: `https://help.mixpanel.com/hc/en-us/articles/360044299311`,
          target: `_blank`,
        }}
      >
        &nbsp;Learn more about sharing
      </a>
    </p>
  </div>
);

export const ShareSection = (title, items, helpers, disabled) => {
  let peopleTitle;
  if (title === `People` && helpers.entityHasOwner()) {
    peopleTitle = GetOwnerListItem(helpers);
  }

  if (items || peopleTitle) {
    return (
      <div sel='.share-section' attrs={{elref: `shareSection${title}`}}>
        <header>{title}</header>
        <ul sel='.share-list'>
          {peopleTitle}
          {items?.map((share) => {
            // in editor mode, when a user adds a new member or team we need to hydrate metadata
            // using data from granted_users/granted_teams endpoints. In case of disabled state,
            // we avoid this hydration since it has all metadata needed from shared-entities endpoint.
            const shareGrant = disabled ? share : helpers.existingShares.getShareItem(share);

            let subtitleContent: string;
            if (title === `People`) {
              subtitleContent = helpers.getShareGrantEmail(shareGrant.email);
            } else if (!disabled) {
              subtitleContent = `${helpers.existingShares.getTeamMembersCount(shareGrant)} People`;
            }

            return (
              <li attrs={{elref: `share_${shareGrant.id}`}}>
                {getUserIconEl(shareGrant.id, helpers.getShareGrantDisplayName(shareGrant), shareGrant.email)}
                <div sel='.display'>
                  <div sel='.name-wrapper'>
                    <div sel='.name'>{helpers.getShareGrantDisplayName(shareGrant)}</div>
                    {helpers.getShareGrantStatusText(shareGrant.grantStatus) && (
                      <TextHighlightEl
                        attrs={{
                          theme: `success`,
                          text: helpers.getShareGrantStatusText(shareGrant.grantStatus),
                        }}
                      />
                    )}
                  </div>
                  {subtitleContent && <div sel='.subtitle'>{subtitleContent}</div>}
                </div>
                <div sel='.controls'>
                  <SelectEl
                    sel='.share-level'
                    attrs={{
                      'disable-search': true,
                      'menu-width': 120,
                      theme: SelectTheme.Tertiary,
                      'always-show-caret': true,
                      sections: helpers.getSelectOptions(ShareOptionsLocation.UserOrTeamShare),
                      'selected-option': share.canEdit || !helpers.isSharingEnabledForProject() ? `Editor` : `Viewer`,
                      elref: `selectShareLevel_${shareGrant.id}`,
                      'use-fixed-dropmenu-pos': true,
                      disabled,
                    }}
                    on={{
                      change: (ev) => helpers.existingShares.handleShareLevelSelectChange(ev, shareGrant),
                    }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  return null;
};
